<template>
  <div class="ckp" data-pfe-menu-root @keydown.esc="onEscape">
    <div
      class="ckp-trigger"
      :class="{ 'ckp-trigger--open': open }"
      role="button"
      tabindex="0"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="ariaLabel"
      :data-test="tid('trigger')"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <template v-if="display === 'chips'">
        <span v-for="c in chips" :key="c.value" class="ckp-chip" :title="c.name" :data-test="tid('chip')">
          <span class="ckp-chip-text">{{ c.name }}</span>
          <button
            type="button"
            class="ckp-chip-x"
            :aria-label="'Remove ' + c.name"
            :data-test="tid('chip-remove')"
            @click.stop="remove(c.value)"
          ><i class="fa fa-times" aria-hidden="true"></i></button>
        </span>
        <span v-if="chips.length === 0" class="ckp-placeholder" :data-test="tid('placeholder')">{{ placeholder }}</span>
      </template>
      <span v-else class="ckp-text" :class="{ 'ckp-text--empty': chips.length === 0 }" :data-test="tid('summary')">{{ summary }}</span>
      <i class="fa fa-chevron-down ckp-chev" aria-hidden="true"></i>
    </div>

    <pop-menu v-if="open" :max-height="360" :min-width="280" :data-test="tid('menu')">
      <template v-if="searchable" #header>
        <div class="ckp-search">
          <i class="fa fa-search" aria-hidden="true"></i>
          <input
            ref="search"
            v-model="query"
            type="text"
            class="ckp-search-input"
            :placeholder="searchPlaceholder"
            :aria-label="searchPlaceholder"
            :data-test="tid('search')"
          />
        </div>
      </template>
      <button
        v-for="o in filtered"
        :key="o.value"
        type="button"
        role="option"
        class="ckp-option"
        :aria-selected="isPicked(o.value) ? 'true' : 'false'"
        :data-test="tid('option')"
        :data-value="o.value"
        @click="toggleOption(o.value)"
      >
        <span class="ckp-box" :class="{ 'ckp-box--on': isPicked(o.value) }" aria-hidden="true"><i v-if="isPicked(o.value)" class="fa fa-check"></i></span>
        <span class="ckp-name">{{ o.name }}</span>
        <span v-if="o.tag" class="ckp-tag" :data-test="tid('option-tag')">{{ o.tag }}</span>
      </button>
      <div v-if="filtered.length === 0" class="ckp-empty" :data-test="tid('none')">
        <template v-if="query.trim()">No matches for &ldquo;{{ query.trim() }}&rdquo;.</template>
        <template v-else>{{ emptyText }}</template>
      </div>
    </pop-menu>
  </div>
</template>

<script>
import PopMenu from './PopMenu.vue';

/**
 * A multi-select with check marks — Forms Settings' provider list ("Any provider") and "What counts as an update" (the forms that restart the
 * Medical History clock). Board FormsSettings.dc.html: a trigger that shows the picks (chips, or one line of text), and a searchable menu where
 * every row is a check box + a name (+ a muted type tag). The menu stays open while you tick, like the board; Escape / a click outside closes it.
 *
 * `value` is the picked values (strings); `options` = [{ value, name, tag? }]. A picked value the options do not know (a form since deleted, a provider
 * since hidden) still shows, named from `fallbackNames` ({ value: name }) or the bare value — it is never filtered away. Emits `input` with the new
 * array. Same popover shell, scrim-less outside-click and Escape discipline as CodePicker (the editor closes on Escape too: a menu answers it first).
 */
export default {
  name: 'PfeCheckPicker',
  components: { PopMenu },
  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    /** 'chips' = one removable chip per pick; 'text' = one line ("Dr. Patel, Dr. Okafor"). */
    display: { type: String, default: 'chips', validator: (v) => v === 'chips' || v === 'text' },
    placeholder: { type: String, default: 'Pick…' },
    searchPlaceholder: { type: String, default: 'Search' },
    searchable: { type: Boolean, default: true },
    ariaLabel: { type: String, default: 'Pick' },
    /** Shown when the menu has nothing at all to list (no query). */
    emptyText: { type: String, default: 'Nothing to pick yet.' },
    fallbackNames: { type: Object, default: () => ({}) },
    /** Prefix of every data-test ('<id>-trigger', '<id>-option', …). */
    testId: { type: String, default: 'pfe-check' },
  },
  data() {
    return { open: false, query: '' };
  },
  computed: {
    picked() { return (Array.isArray(this.value) ? this.value : []).map(String); },
    chips() {
      return this.picked.map((value) => {
        const o = this.options.find((x) => String(x.value) === value);
        return { value, name: (o && o.name) || this.fallbackNames[value] || value };
      });
    },
    summary() { return this.chips.length ? this.chips.map((c) => c.name).join(', ') : this.placeholder; },
    filtered() {
      const q = this.query.trim().toLowerCase();
      return this.options.filter((o) => !q || String(o.name || '').toLowerCase().includes(q) || String(o.tag || '').toLowerCase().includes(q));
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onOutside, true);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside, true);
  },
  methods: {
    tid(name) { return `${this.testId}-${name}`; },
    isPicked(value) { return this.picked.indexOf(String(value)) !== -1; },
    toggle() {
      if (this.open) { this.close(); return; }
      this.open = true;
      this.query = '';
      this.$nextTick(() => { if (this.$refs.search) this.$refs.search.focus(); });
    },
    close() { this.open = false; },
    onOutside(e) {
      if (this.open && this.$el && !this.$el.contains(e.target)) this.close();
    },
    onEscape(e) {
      if (!this.open) return;
      e.preventDefault();
      e.stopPropagation();
      this.close();
    },
    toggleOption(value) {
      const v = String(value);
      this.$emit('input', this.isPicked(v) ? this.picked.filter((x) => x !== v) : this.picked.concat([v]));
    },
    remove(value) {
      this.$emit('input', this.picked.filter((x) => x !== String(value)));
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.ckp { position: relative; min-width: 0; }

.ckp-trigger {
  @include pfe-field(42px);
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px 34px 6px 6px;
  cursor: pointer;

  &:focus-visible, &--open { @include pfe-focus-ring; }
}
.ckp-chev { position: absolute; right: 12px; top: 14px; font-size: 12px; color: $pfe-neutral-400; }
.ckp-placeholder { padding: 0 6px; font-size: 14px; color: $pfe-neutral-400; }
.ckp-text { padding: 0 6px; font-size: 14px; color: $pp-text; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ckp-text--empty { color: $pfe-neutral-500; }
.ckp-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
  height: 28px;
  padding: 0 3px 0 10px;
  border-radius: 999px;
  background: $pp-brand-subtle;
  color: $pp-brand-deep;
  font-size: 13px;
  font-weight: 600;
}
.ckp-chip-text { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ckp-chip-x {
  flex: none;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: $pp-brand-deep;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: $pp-brand-border; }
}

.ckp-search { display: flex; align-items: center; gap: 8px; padding-left: 6px; color: $pfe-neutral-400; }
.ckp-search-input {
  flex: 1;
  min-width: 0;
  height: 34px;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 14px;
  color: $pp-text;

  &:focus { outline: none; }
}
.ckp-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  font-size: 14px;
  text-align: left;
  color: $pp-text;
  cursor: pointer;

  &:hover, &:focus-visible { background: $pfe-neutral-50; outline: none; }
}
.ckp-box {
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid $pp-border-strong;
  background: $pp-surface;
  color: #fff;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  &--on { background: $pp-brand; border-color: $pp-brand; }
}
.ckp-name { flex: 1; min-width: 0; }
.ckp-tag {
  flex: none;
  padding: 2px 8px;
  border-radius: 999px;
  background: $pfe-neutral-50;
  font-size: 11.5px;
  font-weight: 600;
  color: $pfe-neutral-600;
  white-space: nowrap;
}
.ckp-empty { padding: 14px 10px; font-size: 13.5px; color: $pfe-neutral-500; }
</style>
